import {
  KeyboardEventHandler,
  MouseEventHandler,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent
} from "react";
import { css } from "@emotion/react";
import { styles } from "./styles";
import { useFocusOnMount, useSizingProp, useResizeObserver } from "../../hooks";
import { ChipProps, DoNotCare, DropdownProps } from "../../types";
import {
  isPropsMultiSelect,
  isPropsSingleSelect,
  SelectTargetProps
} from "./types";
import Chips, { ChipsSetFocusRef } from "../Chips";
import { EMPTY_SELECT_TEXT } from "./utils";
import SelectClearButton from "./ClearButton";
import * as Icons from "../Icons";

const SelectTarget = <
  Selected extends string | undefined | string[],
  Item extends string | Record<string, DoNotCare>
>({
  value,
  options,
  focusOnMount,
  selectedIds,
  itemsMap,
  onChange,
  lookupLabel,
  lookupIntent,
  lookupIcon,
  selectedSeparator,
  label,
  fixed,
  readonly,
  fill,
  fillToWidestOption,
  disabled,
  error,
  cannotClear,
  width: incomingWidth,
  minWidth: incomingMinWidth,
  isOpen,
  intent,
  onFocus,
  onBlur,
  onClick,
  setTargetWidth,
  setFocusRef,
  noWrap
}: SelectTargetProps<Selected, Item>) => {
  const targetRef = useRef<HTMLDivElement>(null);

  useFocusOnMount(targetRef, !!focusOnMount);

  const rawWidth = useSizingProp(incomingWidth, "auto");
  const minWidth = useSizingProp(incomingMinWidth, "auto");

  const selectProps: Pick<
    DropdownProps<Selected, Item>,
    "value" | "onChange"
  > = {
    value,
    onChange
  };

  let setFocusChipRef: ChipsSetFocusRef | null = null;

  useImperativeHandle(setFocusRef, () => ({
    chipIndex: setFocusChipRef,
    target: () => targetRef.current?.focus()
  }));

  const onTargetClick: MouseEventHandler<HTMLElement> = e => {
    e.stopPropagation();
    e.preventDefault();
    if (disabled) {
      return;
    }
    onClick?.(e);
  };

  const onChipsBlur = (direction: "next" | "prev") => {
    switch (direction) {
      case "next":
        onBlur(isOpen ? "next" : undefined);
        break;
      case "prev":
        onBlur("prev");
        break;
      default:
        break;
    }
  };

  const onTargetKeyDown: KeyboardEventHandler<HTMLElement> = e => {
    if (disabled) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    switch (e.key) {
      case "ArrowUp":
        onBlur("prev");
        break;
      case "ArrowDown": {
        onBlur("next");
        break;
      }
      case "Tab": {
        if (e.shiftKey) {
          onBlur("prev");
        } else {
          onBlur(isOpen ? "next" : undefined);
        }
        break;
      }
      case " ":
      case "Enter":
        onBlur(isOpen ? "prev" : "next");
        break;
      case "Escape":
        e.preventDefault();
        e.stopPropagation();
        onBlur("prev");
        break;
      case "Backspace":
      case "Delete": {
        if (cannotClear || disabled) {
          break;
        }
        if (isPropsSingleSelect(selectProps)) {
          selectProps.onChange?.(undefined, []);
        } else if (isPropsMultiSelect(selectProps)) {
          const nextIds = [...(fixed?.values() || [])];
          selectProps.onChange?.(
            nextIds,
            nextIds.map(id => itemsMap.get(id)).filter((t): t is Item => !!t)
          );
        }
        onBlur("next");
        break;
      }
      default:
        break;
    }
  };

  const measureRef = useRef<HTMLDivElement>(null);
  const [measuredTextWidth, setMeasuredTextWidth] = useState<number>();
  // When true the hidden option-label spans are rendered so the layout
  // effect below can measure them; set back to false immediately after
  // measurement so they're removed from the DOM before the browser paints.
  const [measuring, setMeasuring] = useState(!!fillToWidestOption);

  useLayoutEffect(() => {
    if (!measuring) {
      return;
    }
    const container = measureRef.current;
    if (!container) {
      return;
    }
    const widest = Array.from(container.children).reduce(
      (max, child) => Math.max(max, child.getBoundingClientRect().width),
      0
    );
    if (widest) {
      setMeasuredTextWidth(widest);
    }
    setMeasuring(false);
  }, [measuring]);

  useEffect(() => {
    if (!fillToWidestOption) {
      return undefined;
    }
    let cancelled = false;
    // Web fonts may still be loading (falling back to a narrower font) at
    // mount; trigger a second measure pass once the real fonts are ready.
    document.fonts?.ready?.then(() => {
      if (!cancelled) {
        setMeasuring(true);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [fillToWidestOption]);

  const width = useMemo(
    () => {
      if (fill) {
        return "100%";
      }
      if (fillToWidestOption) {
        if (!measuredTextWidth) {
          return "fit-content";
        }
        // Add the target's own chrome via its real tokens (border, padding,
        // gap to the chevron/clear icon, and the icon itself) so this
        // always matches, even if those tokens change — see `target` (which
        // is box-sizing: border-box, so this total becomes the box's actual
        // width) and `iconSizing` in ./styles.ts.
        return `calc(${Math.ceil(measuredTextWidth)}px + 2px + var(--spacing-4) * 2 + var(--spacing-16) + var(--spacing-24))`;
      }
      if (rawWidth) {
        return rawWidth;
      }
      return undefined;
    },
    /**
     * Options is deliberately left out of the dependencies array
     * Reason is that on isLoading new options the size of the target might shift and create a negative user experience.
     * The first set of options will define the size of the target and the size will not change on new options.
     * TODO: to be verified with the Searchable logic in situation when isLoading comes first
     */
    [rawWidth, fill, fillToWidestOption, readonly, measuredTextWidth]
  );

  useResizeObserver(targetRef, () =>
    setTargetWidth(targetRef.current?.offsetWidth || 100)
  );

  useEffect(() => {
    setTargetWidth(targetRef.current?.offsetWidth || 100);
  }, [width, readonly]);

  const clearable = selectedIds.length > (fixed?.size || 0);

  const ToggleIcon = (
    <div
      className="kit-select-target-toggle-icon"
      css={[
        styles.toggleIcon,
        styles.iconSizing,
        css`
          transform: rotate(${isOpen ? "180deg" : "0deg"});
        `
      ]}
    >
      <Icons.Chevron direction="down" />
    </div>
  );

  const lookupLabelFromId = (id: string | undefined) => {
    const foundItem = !!id && itemsMap?.get(id);
    if (foundItem) {
      return foundItem ? lookupLabel(foundItem) : undefined;
    }
    return undefined;
  };

  const lookupIconFromId = (id: string | undefined) => {
    const foundItem = !!id && itemsMap?.get(id);
    if (foundItem) {
      return foundItem ? lookupIcon?.(foundItem) : undefined;
    }
    return undefined;
  };

  const lookupReadonly = (id: string | undefined) => {
    return id ? !!fixed?.has(id) : false;
  };

  const lookupIntentFromId = (id: string | undefined) => {
    const foundItem = !!id && itemsMap?.get(id);
    if (foundItem) {
      return foundItem
        ? (lookupIntent?.(foundItem) as ChipProps["intent"])
        : undefined;
    }
    return undefined;
  };

  const onSelectedChipKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    switch (e.key) {
      case "ArrowUp":
        onChipsBlur("prev");
        break;
      case "ArrowDown":
      case "Enter":
      case " ": {
        e.preventDefault();
        e.stopPropagation();
        onChipsBlur("next");
        break;
      }
      default:
        break;
    }
  };

  return (
    <>
      {measuring && (
        <div ref={measureRef} css={styles.hiddenMeasure} aria-hidden>
          {options.map((option, index) => (
            <span key={index} css={styles.targetText}>
              {lookupLabel(option)}
            </span>
          ))}
        </div>
      )}
      <div
        aria-label={label}
        ref={targetRef}
        css={[
          styles.target,
          !!selectedIds.length && styles.hasSelection,
          !!error && styles.hasError,
          !!disabled && styles.isDisabled,
          css`
            width: ${width};
            min-width: ${minWidth};
          `
        ]}
        className="subKit-SelectTarget"
        role="button"
        aria-expanded={isOpen}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        onClick={onTargetClick}
        onFocus={onFocus}
        onKeyDown={onTargetKeyDown}
      >
        {isPropsMultiSelect(selectProps) && (
          <div
            className="kit-select-multi-target"
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: ${value?.length ? "flex-start" : "center"};
            `}
          >
            <Chips
              items={selectedIds}
              setFocusRef={ref => (setFocusChipRef = ref)}
              onChipsBlur={onChipsBlur}
              onChange={ids =>
                selectProps.onChange(
                  ids,
                  ids.map(id => itemsMap.get(id)).filter((t): t is Item => !!t)
                )
              }
              intent={intent}
              lookupId={id => id}
              lookupLabel={lookupLabelFromId}
              lookupIcon={lookupIconFromId}
              lookupIntent={lookupIntentFromId}
              lookupReadonly={lookupReadonly}
              onClick={e => e.stopPropagation()}
              disabled={disabled}
              onKeyDown={onSelectedChipKeyDown}
              emptyContent={
                <span css={styles.targetText}>{EMPTY_SELECT_TEXT}</span>
              }
              separator={selectedSeparator}
              noWrap={noWrap}
            />
            {clearable ? (
              <SelectClearButton onClick={() => selectProps.onChange([], [])} />
            ) : (
              ToggleIcon
            )}
          </div>
        )}
        {isPropsSingleSelect(selectProps) && (
          <div
            className="kit-select-single-target"
            css={css`
              width: 100%;
              display: flex;
              justify-content: space-between;
              align-items: center;
            `}
          >
            <span css={styles.targetText}>
              {lookupLabelFromId(selectProps.value) || EMPTY_SELECT_TEXT}
            </span>
            {!cannotClear && !!selectProps.value ? (
              <SelectClearButton
                onClick={() => selectProps.onChange?.(undefined, [])}
              />
            ) : (
              ToggleIcon
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default SelectTarget;
